import { Button, Dialog, MobileDrawer } from "@jugl-web/ui-kit";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, useCallback } from "react";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

export interface UpdateTicketContactDialogProps {
  isOpen: boolean;
  currentContactName: string;
  nextContactName: string;
  onClose: () => void;
  onConfirm: () => void;
}

export const UpdateTicketContactDialog: FC<UpdateTicketContactDialogProps> = ({
  isOpen,
  currentContactName,
  nextContactName,
  onClose,
  onConfirm,
}) => {
  const { isMobile } = useAppVariant();
  const { t } = useTranslations();

  const handleConfirm = useCallback(() => {
    onConfirm();
    onClose();
  }, [onClose, onConfirm]);

  const title = t({
    id: "client-support-page.update-ticket-customer-emoji",
    defaultMessage: "💼 Update Ticket Customer",
  });

  const description = t(
    {
      id: "client-support-page.update-ticket-customer-description",
      defaultMessage:
        "Once you confirm this action, the ticket will be removed from {currentContact} account, and associated with {nextContact} instead",
    },
    {
      currentContact: (
        <span className="font-medium text-primary">{currentContactName}</span>
      ),
      nextContact: (
        <span className="font-medium text-primary">{nextContactName}</span>
      ),
    }
  );

  const cancelLabel = t({
    id: "common.cancel",
    defaultMessage: "Cancel",
  });

  const confirmLabel = t({
    id: "common.confirm",
    defaultMessage: "Confirm",
  });

  if (isMobile) {
    return (
      <MobileDrawer
        isOpen={isOpen}
        size="auto"
        header={{ title }}
        onClose={onClose}
      >
        <MobileDrawer.Content>
          <span className="font-secondary text-sm leading-[21px] text-dark-700">
            {description}
          </span>
        </MobileDrawer.Content>
        <MobileDrawer.Actions>
          <div className="flex gap-4 pb-8">
            <Button variant="tertiary" onClick={onClose} className="flex-1">
              {cancelLabel}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              className="flex-1"
            >
              {confirmLabel}
            </Button>
          </div>
        </MobileDrawer.Actions>
      </MobileDrawer>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className="flex w-[480px] flex-col overflow-hidden rounded-2xl p-10"
    >
      <span className="font-secondary text-[22px] font-bold text-dark">
        {title}
      </span>
      <span className="mt-2.5 font-secondary text-sm leading-[21px] text-dark-700">
        {description}
      </span>
      <div className="mt-14 flex items-center gap-4">
        <Button onClick={onClose} variant="tertiary" className="w-[140px]">
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          iconEnd={<ArrowRightIcon />}
          className="flex-1"
        >
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
};
